/* eslint-disable no-param-reassign */
import { createAction, createSlice, createSelector } from '@reduxjs/toolkit';

import createRequestActionTypes from 'store/helpers/createRequestActionTypes';
import {
  onFailure,
  onSuccessList,
  handleClearErrors,
  onSuccessDetail,
} from 'store/helpers/commonHandlers';
import getSplicedList from 'lib/utils/getUpdatedList';
import {
  ASSIGN_PROTOCOL_SUCCESS,
  UPDATE_ASSIGNED_PROTOCOL_SUCCESS,
  DELETE_ASSIGNED_PROTOCOL_SUCCESS,
} from 'features/forms';

const name = 'patients';

/** ActionType for Request */
export const [
  GET_PATIENT_LIST,
  GET_PATIENT_LIST_SUCCESS,
  GET_PATIENT_LIST_FAILURE,
] = createRequestActionTypes(name, 'GET_PATIENT_LIST');
export const [
  GET_PATIENT_SUMMARY_LIST,
  GET_PATIENT_SUMMARY_LIST_SUCCESS,
  GET_PATIENT_SUMMARY_LIST_FAILURE,
] = createRequestActionTypes(name, 'GET_PATIENT_SUMMARY_LIST');
export const [
  GET_PATIENT_DETAIL,
  GET_PATIENT_DETAIL_SUCCESS,
  GET_PATIENT_DETAIL_FAILURE,
] = createRequestActionTypes(name, 'GET_PATIENT_DETAIL');
export const [
  GET_NO_DOCTOR_PATIENT_LIST,
  GET_NO_DOCTOR_PATIENT_LIST_SUCCESS,
  GET_NO_DOCTOR_PATIENT_LIST_FAILURE,
] = createRequestActionTypes(name, 'GET_NO_DOCTOR_PATIENT_LIST');
export const [
  ASSIGN_DOCTOR,
  ASSIGN_DOCTOR_SUCCESS,
  ASSIGN_DOCTOR_FAILURE,
] = createRequestActionTypes(name, 'ASSIGN_DOCTOR');
export const [
  UPDATE_PATIENT_PROFILE,
  UPDATE_PATIENT_PROFILE_SUCCESS,
  UPDATE_PATIENT_PROFILE_FAILURE,
] = createRequestActionTypes(name, 'UPDATE_PATIENT_PROFILE');
export const [
  SEND_REGISTER_REQUEST,
  SEND_REGISTER_REQUEST_SUCCESS,
  SEND_REGISTER_REQUEST_FAILURE,
] = createRequestActionTypes(name, 'SEND_REGISTER_REQUEST');

const slice = createSlice({
  name,
  initialState: {
    list: null,
    summaryList: null,
    noDoctorList: null,
    patientDetails: {},
    next: {},
    count: {},
    errors: {},
  },
  reducers: {
    clearErrors: handleClearErrors,
  },
  extraReducers: {
    [GET_PATIENT_LIST_SUCCESS]: onSuccessList('list'),
    [GET_PATIENT_LIST_FAILURE]: onFailure('list'),
    [GET_PATIENT_SUMMARY_LIST_SUCCESS]: onSuccessList('summaryList'),
    [GET_PATIENT_SUMMARY_LIST_FAILURE]: onFailure('summaryList'),
    [GET_PATIENT_DETAIL_SUCCESS]: onSuccessDetail('patientDetails', 'id'),
    [GET_PATIENT_DETAIL_FAILURE]: onFailure('patientDetails'),
    [GET_NO_DOCTOR_PATIENT_LIST_SUCCESS]: onSuccessList('noDoctorList'),
    [GET_NO_DOCTOR_PATIENT_LIST_FAILURE]: onFailure('noDoctorList'),
    [ASSIGN_DOCTOR_FAILURE]: onFailure('assignDoctor'),
    [ASSIGN_DOCTOR_SUCCESS]: (state, { payload }) => {
      state.noDoctorList = getSplicedList(state.noDoctorList, payload, {
        exchange: false,
      });
      state.list = [...state.list, payload];
      state.errors.assignDoctor = null;
    },
    [UPDATE_PATIENT_PROFILE_FAILURE]: onFailure('updatePatientProfile'),
    [UPDATE_PATIENT_PROFILE_SUCCESS]: (state, { payload }) => {
      state.noDoctorList = getSplicedList(state.noDoctorList, payload, {
        exchange: false,
      });
      state.list = getSplicedList(state.list, payload);
      state.errors.updatePatientProfile = null;
    },
    [SEND_REGISTER_REQUEST_FAILURE]: onFailure('sendRegisterRequest'),
    // Other feature
    [ASSIGN_PROTOCOL_SUCCESS]: (state, { payload }) => {
      if (state.list) {
        state.list = getSplicedList(state.list, payload);
      }
    },
    [UPDATE_ASSIGNED_PROTOCOL_SUCCESS]: (state, { payload }) => {
      if (state.list) {
        state.list = getSplicedList(state.list, payload);
      }
    },
    [DELETE_ASSIGNED_PROTOCOL_SUCCESS]: (state, { payload }) => {
      const { id } = payload;
      if (state.list) {
        const nextList = [...state.list];
        const target = nextList.find((p) => p.id === id);
        if (target) {
          target.protocol_id = null;
          target.protocol_name = null;
        }
        state.list = nextList;
      }
    },
  },
});

/** Action for Request */
export const getPatientList = createAction(GET_PATIENT_LIST);
export const getPatientSummaryList = createAction(GET_PATIENT_SUMMARY_LIST);
export const getPatientDetail = createAction(GET_PATIENT_DETAIL);
export const getNoDoctorPatientList = createAction(GET_NO_DOCTOR_PATIENT_LIST);
export const assignDoctor = createAction(ASSIGN_DOCTOR);
export const updatePatientProfile = createAction(UPDATE_PATIENT_PROFILE);
export const sendRegisterRequest = createAction(SEND_REGISTER_REQUEST);

/** General Action */
export const { clearErrors } = slice.actions;

/** Reducer */
export const patients = slice.reducer;

/** Selectors */
const errorSelector = createSelector(
  (state) => state.errors,
  (errors) => ({ errors }),
);
const listSelector = createSelector(
  (state) => state.list,
  (state) => state.count.list,
  (list, count) => ({
    allPatient: list,
    allCount: count,
  }),
);
const summaryListSelector = createSelector(
  (state) => state.summaryList,
  (state) => state.count.summaryList,
  (summaryList, summaryListCount) => ({
    summaryList,
    summaryListCount,
  }),
);
const patientDetailSelector = createSelector(
  (state) => state.patientDetails,
  (patientDetails) => ({ patientDetails }),
);
const noDoctorSeletor = createSelector(
  (state) => state.noDoctorList,
  (state) => state.count.noDoctorList,
  (noDoctorList, count) => ({ noDoctorList, noDoctorCount: count }),
);
export const selectors = {
  errors: (state) => errorSelector(state[name]),
  list: (state) => listSelector(state[name]),
  summaryList: (state) => summaryListSelector(state[name]),
  patientDetail: (state) => patientDetailSelector(state[name]),
  noDoctorList: (state) => noDoctorSeletor(state[name]),
};

export default slice;
